import React, { useEffect, useState } from "react";
import "../PositionCodes.css";
import { PositionCode } from "../../../interfaces/positionCode";
import { Service } from "../../../interfaces/service";
import {
  conflictRules,
  getDefaultPositionCodes,
  getServiceIDsForDefaultPositionCode,
  getServiceIDsForUCCDefaultPositionCode,
  getServiceIDsForUUADefaultPositionCode,
  getUCCDefaultPositionCodes,
  getUUADefaultPositionCodes,
} from "../../../services/positionCodeService";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllServices } from "../../../services/serviceService";
import { useRegistrationContext } from "../../../contexts/ClientRegistrationContext";
import { sortServices } from "../../../services/helpers";
import ConfirmPopup from "../../../components/client-components/ConfirmPopup";

const DefaultPositionCodes = () => {
  const { data, updateData } = useRegistrationContext();
  const {
    client,
    selectedPosCodeType,
    positionCodes,
    selectedServices,
    creditReportForm,
    SSNTraceForm,
    signatureForm,
  } = data;
  const [errorMessage, setErrorMessage] = useState("");
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const [showSignatureUpload, setShowSignatureUpload] = useState(false);
  const [showSSNUpload, setShowSSNUpload] = useState(false);
  const [showCreditUpload, setShowCreditUpload] = useState(false);
  const navigate = useNavigate();
  const [monitoringToRevert, setMonitoringToRevert] = useState<{
    positionCodeID: number;
    serviceID: number;
  } | null>(null);

  const location = useLocation();
  const newSelectedPosCodeType: string | undefined =
    location.state?.selectedPosCodeType;
  const effectivePosCodeType = newSelectedPosCodeType
    ? newSelectedPosCodeType.toLowerCase()
    : selectedPosCodeType
      ? selectedPosCodeType.toLowerCase()
      : "";

  useEffect(() => {
    const fetchDefaultPositionCodes = async () => {
      try {
        const allServices = await getAllServices();
        const sortedServices = await sortServices(allServices);
        const filteredServices = sortedServices.filter(
          (service) => service.clientVisible === true,
        );
        setAllServices(filteredServices);

        if (
          data.positionCodes.length === 0 ||
          !data.selectedPosCodeType ||
          data.selectedPosCodeType.toLowerCase() !== effectivePosCodeType
        ) {
          updateData({
            positionCodes: [],
            selectedServices: new Map(),
            selectedPosCodeType: effectivePosCodeType,
          });

          let codes: PositionCode[] = [];
          let getServiceIDsFunc:
            | ((positionCodeID: number) => Promise<number[]>)
            | null = null;

          if (effectivePosCodeType === "scratch") {
            codes = [
              {
                positionCodeID: Date.now() % 2147483647,
                clientID: 0,
                positionCodeTitle: "",
                fcraCompliance: true,
                criminalLookbackYears: 7,
                referenceLookbackYears: 10,
                requiresCandidatePayment: false,
              },
            ];
          } else if (effectivePosCodeType === "uua") {
            codes = await getUUADefaultPositionCodes();
            getServiceIDsFunc = getServiceIDsForUUADefaultPositionCode;
          } else if (effectivePosCodeType === "ucc") {
            codes = await getUCCDefaultPositionCodes();
            getServiceIDsFunc = getServiceIDsForUCCDefaultPositionCode;
          } else if (effectivePosCodeType === "general") {
            codes = await getDefaultPositionCodes();
            getServiceIDsFunc = getServiceIDsForDefaultPositionCode;
          } else {
            codes = [
              {
                positionCodeID: Date.now() % 2147483647,
                clientID: 0,
                positionCodeTitle: "",
                fcraCompliance: true,
                criminalLookbackYears: 7,
                referenceLookbackYears: 10,
                requiresCandidatePayment: false,
              },
            ];
          }

          updateData({
            positionCodes: codes,
            selectedPosCodeType: effectivePosCodeType,
          });

          if (codes.length > 0 && getServiceIDsFunc) {
            const serviceMap = new Map<number, Set<number>>();
            for (const positionCode of codes) {
              const serviceIDs = await getServiceIDsFunc(
                positionCode.positionCodeID,
              );
              serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
            }
            updateData({ selectedServices: new Map(serviceMap) });
          } else {
            updateData({ selectedServices: new Map() });
          }
        }
      } catch (err: any) {
        console.error(
          "Failed to fetch default position codes or services:",
          err,
        );
      }
    };

    fetchDefaultPositionCodes();
  }, [
    data.positionCodes,
    data.selectedServices,
    data.selectedPosCodeType,
    selectedPosCodeType,
    newSelectedPosCodeType,
    effectivePosCodeType,
    updateData,
  ]);

  // Redirect to registration if data not there
  useEffect(() => {
    if (!data.userAgreement) {
      navigate("/registration");
    }
  }, [data.userAgreement, navigate]);

  useEffect(() => {
    const checkForSpecialServices = () => {
      const ssnTraceID = allServices.find(
        (service) => service.serviceID === 17,
      )?.serviceID;
      const creditReportID = allServices.find(
        (service) => service.serviceID === 16,
      )?.serviceID;
      const referenceServiceIDs = allServices
        .filter((service) => service.referenceYears !== null)
        .map((service) => service.serviceID);

      if (ssnTraceID) {
        const isSSNTraceSelected = Array.from(selectedServices.values()).some(
          (serviceSet) => serviceSet.has(ssnTraceID),
        );
        setShowSSNUpload(isSSNTraceSelected);
      }

      if (creditReportID) {
        const isCreditReportSelected = Array.from(
          selectedServices.values(),
        ).some((serviceSet) => serviceSet.has(creditReportID));
        setShowCreditUpload(isCreditReportSelected);
      }

      if (referenceServiceIDs.length > 0) {
        const isReferenceSelected = Array.from(selectedServices.values()).some(
          (serviceSet) =>
            referenceServiceIDs.some((referenceID) =>
              serviceSet.has(referenceID),
            ),
        );
        setShowSignatureUpload(isReferenceSelected);
      }
    };

    checkForSpecialServices();
  }, [selectedServices, allServices]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 3000);
      return () => clearTimeout(timer);
    }
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, successMessage]);

  const handleCheckboxChange = (positionCodeID: number, serviceID: number) => {
    const newSelectedServices = new Map<number, Set<number>>(
      data.selectedServices,
    );

    const selectedServiceSet =
      newSelectedServices.get(positionCodeID) || new Set<number>();

    const isSelected = selectedServiceSet.has(serviceID);
    const conflictingServices = conflictRules[serviceID] || [];

    if (!isSelected) {
      const foundConflict = conflictingServices.find((conflictID) =>
        selectedServiceSet.has(conflictID),
      );

      if (foundConflict) {
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [positionCodeID]: `You have a conflicting selected service.`,
        }));

        return;
      }

      // confirm that they want monitoring
      if (serviceID === 36) {
        setMonitoringToRevert({ positionCodeID, serviceID });
      }
    }

    if (isSelected) {
      selectedServiceSet.delete(serviceID);
    } else {
      selectedServiceSet.add(serviceID);
    }

    newSelectedServices.set(positionCodeID, selectedServiceSet);

    // Clear errors
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [positionCodeID]: "",
    }));

    updateData({
      selectedServices: newSelectedServices,
    });
  };

  const handleNavigateToConfirmation = async () => {
    if (await validateFields()) {
      navigate("/client/registration/confirmation");
      window.scrollTo(0, 0);
    }
  };

  const handleDelete = (index: number) => {
    try {
      const newPositionCodes = positionCodes.filter((_, i) => i !== index);
      updateData({ positionCodes: newPositionCodes });

      const newSelectedServices = new Map(selectedServices);
      const positionCodeID = positionCodes[index].positionCodeID;
      if (positionCodeID) {
        newSelectedServices.delete(positionCodeID);
      }
      updateData({ selectedServices: newSelectedServices });

      const newValidationErrors: { [key: string]: string } = {};
      Object.keys(validationErrors).forEach((key) => {
        const match = key.match(/positionCode(\d+)_/);
        if (match) {
          const currentIndex = parseInt(match[1], 10);
          if (currentIndex < index) {
            newValidationErrors[key] = validationErrors[key];
          } else if (currentIndex > index) {
            const newKey = key.replace(
              `positionCode${currentIndex}_`,
              `positionCode${currentIndex - 1}_`,
            );
            newValidationErrors[newKey] = validationErrors[key];
          }
        }
      });

      setValidationErrors(newValidationErrors);

      setSuccessMessage("Position Code deleted successfully.");
    } catch (error) {
      console.error("Error deleting position code:", error);
      setErrorMessage("Failed to delete Position Code.");
    }
  };

  const handleCreatePositionCode = () => {
    const newPositionCode: PositionCode = {
      positionCodeID: Date.now() % 2147483647,
      clientID: client?.clientID || 0,
      positionCodeTitle: "",
      fcraCompliance: true,
      criminalLookbackYears: 7,
      referenceLookbackYears: 10,
      requiresCandidatePayment: false,
    };

    updateData({
      positionCodes: [...(positionCodes || []), newPositionCode],
    });

    const newSelectedServices = new Map(selectedServices);
    newSelectedServices.set(newPositionCode.positionCodeID, new Set());
    updateData({
      selectedServices: newSelectedServices,
    });

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`positionCode${positionCodes?.length || 0}_positionCodeTitle`]:
        "Title is required.",
    }));
  };

  const handleFormUpload = async (type: number) => {
    try {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      if (type === 1) {
        fileInput.accept = "image/*";
      } else {
        fileInput.accept = "image/*,.pdf";
      }

      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;

        if (type === 2) {
          updateData({ creditReportForm: file });
          setSuccessMessage("Credit Report form added for upload.");
        } else if (type === 3) {
          updateData({ SSNTraceForm: file });
          setSuccessMessage("SSN Trace form added for upload.");
        } else if (type === 1) {
          if (!file.type.startsWith("image/")) {
            setErrorMessage("Signatures must be image type files.");
            return;
          }
          updateData({ signatureForm: file });
          setSuccessMessage("Signature added for upload.");
        }
      };
      fileInput.click();
    } catch (error) {
      console.error("Error handling file upload:", error);
    }
  };

  const renderServices = (positionCodeID: number, disabled: boolean) => (
    <div className="services-list">
      {allServices.map((service) => (
        <div key={service.serviceID} className="service-item">
          <label>
            <input
              style={{ marginRight: "10px" }}
              className={`form-check-input ${
                validationErrors[`positionCode${positionCodeID}_services`]
                  ? "is-invalid"
                  : ""
              }`}
              type="checkbox"
              checked={
                selectedServices.get(positionCodeID)?.has(service.serviceID) ||
                false
              }
              onChange={() =>
                handleCheckboxChange(positionCodeID, service.serviceID)
              }
              disabled={disabled}
            />
            {service.serviceName}
          </label>
        </div>
      ))}
      {validationErrors[`positionCode${positionCodeID}_services`] && (
        <div className="invalid-feedback">
          {validationErrors[`positionCode${positionCodeID}_services`]}
        </div>
      )}
    </div>
  );

  const handlePositionCodeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
    index: number,
  ) => {
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    updateData({
      positionCodes:
        data.positionCodes?.map((positionCode, i) =>
          i === index ? { ...positionCode, [field]: value } : positionCode,
        ) || [],
    });

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`positionCode${index}_${field}`]: "",
    }));
  };

  const validateFields = () => {
    const errors: { [key: string]: string } = {};

    positionCodes?.forEach((positionCode, index) => {
      if (!positionCode.positionCodeTitle) {
        errors[`positionCode${index}_positionCodeTitle`] = "Title is required.";
      }
      if (!positionCode.referenceLookbackYears) {
        errors[`positionCode${index}_referenceLookbackYears`] =
          "Years are required.";
      }

      const selectedServicesForPosition = selectedServices.get(
        positionCode.positionCodeID,
      );
      if (
        !selectedServicesForPosition ||
        selectedServicesForPosition.size === 0
      ) {
        errors[`positionCode${index}_services`] =
          "At least one service must be selected.";
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Step 3.5: Define Position Codes</h1>
      </div>
      <div className="divider"></div>
      <div className="data-container">
        {errorMessage && <div className="error-message">{errorMessage}</div>}

        {effectivePosCodeType === "general" && (
          <div className="text-body">
            For your convenience, examples of default Position Codes commonly
            used by our clients in faith-based organizations are detailed below.
            You may choose to use these defaults or modify them to fit your
            needs. As this may differ from the recommendations of your
            denominational leadership or legal counsel, we encourage you to
            review and adjust the Position Codes as needed to align with your
            organization’s specific guidelines and requirements.
            {/* <a
              href="https://oxfordstoreacctdev.blob.core.windows.net/documents/Public-Records-Check-Fee-Schedule.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              here.
            </a> */}{" "}
            Position Codes can be updated at any time to accommodate changes in
            your screening requirements. If you have any questions or need
            further clarification, please call us at (800) 801-9114 or email us
            at <a href="mailto:request@oxforddoc.com">request@oxforddoc.com</a>.
          </div>
        )}

        {effectivePosCodeType === "ucc" && (
          <div className="text-body">
            As part of our contract with the{" "}
            <b>United Church of Christ (UCC)</b>, the following{" "}
            <b>Position Codes</b> have been adapted in accordance with
            denominational leadership.
            <b> Position Code 1</b> is permanent and is reserved for{" "}
            <b>Clergy</b> and aligns with <b>Profiles Office requirements</b>.
          </div>
        )}

        {effectivePosCodeType === "uua" && (
          <div className="text-body">
            Your denominational leadership has simplified the process by
            proposing the following screening parameters for various leadership
            positions, aligning with{" "}
            <b>UUA "Responsible Staffing" Guidelines</b>. These recommendations
            help ensure appropriate and consistent background screening based on
            role-specific responsibilities.
          </div>
        )}

        <h3 style={{ flex: "0 0 100%" }}>Instructions</h3>
        <div className="text-body">
          Defining Position Codes is a simple process. You can create up to{" "}
          <b>10 Position Codes</b> by providing the following details:
          <ol>
            <li>
              <b>Position Description</b> – e.g., "Paid Staff, works with
              youth."
            </li>
            <li>
              <b>Selected Services</b> – Choose the background check services
              you want performed for this position.
            </li>
          </ol>
          These Position Codes can be modified at any time to fit your
          organization's evolving needs.
        </div>

        {positionCodes && positionCodes.length > 0 ? (
          positionCodes.map((positionCode, index) => (
            <div
              key={positionCode.positionCodeID}
              className="position-codes-group"
            >
              <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
                Position Code {index + 1}
              </div>

              <div className="field-long" style={{ flex: "0 0 60%" }}>
                <label className="field-label">Title:</label>
                <input
                  className={`form-control ${
                    validationErrors[`positionCode${index}_positionCodeTitle`]
                      ? "is-invalid"
                      : ""
                  }`}
                  type="text"
                  value={positionCode.positionCodeTitle || ""}
                  onChange={(e) =>
                    handlePositionCodeChange(e, "positionCodeTitle", index)
                  }
                  disabled={positionCode.requiresCandidatePayment}
                />
                {validationErrors[`positionCode${index}_positionCodeTitle`] && (
                  <div className="invalid-feedback">
                    {validationErrors[`positionCode${index}_positionCodeTitle`]}
                  </div>
                )}
              </div>
              {!positionCode.requiresCandidatePayment && (
                <div
                  style={{
                    flex: "0 0 30%",
                    alignSelf: "end",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <button
                    className="oxbtn btn-red"
                    onClick={() => handleDelete(index)}
                  >
                    Delete
                  </button>
                </div>
              )}

              {/* <div className="field-long" style={{ flex: "0 0 100%" }}>
                <label className="field-label">FCRA Compliance:</label>
                <div>
                  <input
                    className={`form-check-input`}
                    type="checkbox"
                    checked={positionCode?.fcraCompliance || false}
                    onChange={(e) =>
                      handlePositionCodeChange(e, "fcraCompliance", index)
                    }
                    disabled={positionCode.requiresCandidatePayment}
                  />
                </div>
              </div> */}

              <div className="services-container">
                <div className="text-sub-sub-header">
                  Services to be Performed:
                </div>
                {renderServices(
                  positionCode.positionCodeID,
                  positionCode.requiresCandidatePayment,
                )}
                {validationErrors[`${positionCode.positionCodeID}`] && (
                  <div
                    className="invalid-feedback"
                    style={{ display: "block" }}
                  >
                    {validationErrors[`${positionCode.positionCodeID}`]}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <p>No position codes available for this client.</p>
        )}
        {positionCodes.length < 10 && (
          <div className="btn-center" style={{ flex: "0 0 100%" }}>
            <button
              className="oxbtn btn-purple"
              onClick={() => handleCreatePositionCode()}
            >
              Add Additional Position Code{" "}
              <i className="bi bi-plus-lg btn-icon"></i>
            </button>
          </div>
        )}
      </div>
      {showSignatureUpload && (
        <div className="data-container">
          <div className="text-header">Reference Form Signature</div>
          <div className="text-body">
            <b>
              Since you’ve selected a Reference service, a signature is required
              to complete the reference forms.
            </b>{" "}
            You can upload an existing electronic signature image file, or have
            the primary contact sign their name on a blank white sheet of paper
            and upload it as an image type file (.jpeg, .jpg, .heic, .png).
          </div>
          <div className="action-buttons">
            {/* <button className="oxbtn btn-light">Print/View Form</button> */}
            <button
              className="oxbtn btn-grey"
              onClick={() => handleFormUpload(1)}
            >
              Upload Signature
            </button>
            {signatureForm && (
              <a
                href={URL.createObjectURL(signatureForm)}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                View Uploaded Signature
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </a>
            )}
          </div>
          {errorMessage && <div className="error">{errorMessage}</div>}
        </div>
      )}
      {showSSNUpload && (
        <div className="data-container">
          <div className="text-header">SSN Trace Form</div>
          <div className="text-body">
            Since you have selected an SSN Trace service, you are required to
            complete this form. Please complete and upload this form at your
            earliest convenience.
            <b>
              {" "}
              You do not need to do so now, the form will be available after
              registration.
            </b>{" "}
          </div>
          <div className="action-buttons">
            <button
              className="oxbtn btn-light"
              onClick={() =>
                window.open(
                  "https://oxfordstoreacctdev.blob.core.windows.net/documents/SSN Required Forms.pdf",
                  "_blank",
                )
              }
            >
              Print/View Form
            </button>
            <button
              className="oxbtn btn-grey"
              onClick={() => handleFormUpload(3)}
            >
              Upload SSN Trace Form
            </button>
            {SSNTraceForm && (
              <a
                href={URL.createObjectURL(SSNTraceForm)}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                View Completed Form
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </a>
            )}
          </div>
        </div>
      )}
      {showCreditUpload && (
        <div className="data-container">
          <div className="text-header">Credit Report Form</div>
          <div className="text-body">
            Since you have selected a Credit Report service, you are required to
            complete this form. Please complete and upload this form at your
            earliest convenience.
            <b>
              {" "}
              You do not need to do so now, the form will be available after
              registration.
            </b>{" "}
          </div>
          <div className="action-buttons">
            <button
              className="oxbtn btn-light"
              onClick={() =>
                window.open(
                  "https://oxfordstoreacctdev.blob.core.windows.net/documents/Credit Required Forms.pdf",
                  "_blank",
                )
              }
            >
              Print/View Form
            </button>
            <button
              className="oxbtn btn-grey"
              onClick={() => handleFormUpload(2)}
            >
              Upload Credit Report Form
            </button>
            {creditReportForm && (
              <a
                href={URL.createObjectURL(creditReportForm)}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                View Completed Form
                <i className="btn-icon bi bi-box-arrow-up-right"></i>
              </a>
            )}
          </div>
        </div>
      )}
      <div className="btn-center">
        <button
          className="oxbtn btn-green"
          onClick={handleNavigateToConfirmation}
        >
          Continue
        </button>
      </div>
      {monitoringToRevert && (
        <ConfirmPopup
          onConfirm={() => {
            setMonitoringToRevert(null);
          }}
          onCancel={() => {
            handleCheckboxChange(monitoringToRevert.positionCodeID, 36);
            setMonitoringToRevert(null);
          }}
          message={
            <>
              This service is billed at{" "}
              <strong>$9 per candidate per month</strong> and includes ongoing
              daily monitoring. To avoid charges for the following month,
              monitoring must be canceled by the{" "}
              <strong>25th of the current month</strong>.
              <br />
              <br />
              By clicking <strong>Yes, I'm Sure</strong>, you agree to enroll
              selected candidate(s) with this Position Code in Continuous
              Criminal Monitoring. A new{" "}
              <strong>Subscription Candidates</strong> menu option will be
              available to view and manage candidates enrolled in this service.
            </>
          }
          btnColor="green"
        />
      )}
      {successMessage && (
        <div className="alert-message alert alert-success alert-dismissible fade show">
          {successMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show">
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
    </div>
  );
};

export default DefaultPositionCodes;
